/**
 * Shared plumbing of the group-A rate channels (W3a, sub:channels-A): the English gate, per-paragraph
 * prose iteration, exposure vectors and small tally helpers. Channel-specific detection lives in each
 * channel file; nothing here changes framework semantics.
 */
import { type Doc, REGISTER_PROSE } from '../document/types.ts';
import { compiledEnglish } from '../lang/en/index.ts';
import { EN_GATE } from '../lang/index.ts';
import { derivePrior } from './framework.ts';
import { fmt, proseParagraphs, proseSentenceList } from './helpers.ts';
import type { ChannelDef, Family, Measurement, Ramp, RunContext } from './types.ts';

export { compiledEnglish, fmt };

/** Empty measurement used with `abstain`. */
export const abstainMeasurement = (reason: string): Measurement => ({
	abstain: reason,
	k: new Float64Array(0),
	e: new Float64Array(0),
});

/**
 * English gate (C-LANG, §2.12): a document detected as English whose prose function-word share is
 * below `EN_GATE` is not English enough for English-native channels. Returns the abstention reason.
 */
export function englishGate(doc: Doc): string | undefined {
	if (doc.language.code === 'en' && doc.functionWordShare < EN_GATE)
		return `not English enough: function-word share ${fmt(doc.functionWordShare, 2)} < ${EN_GATE}`;
	return undefined;
}

/** One prose paragraph's own block: its paragraph index, absolute start offset and text. */
export interface ProseBlock {
	p: number;
	base: number;
	text: string;
}

/** The prose paragraphs' own blocks in document order (structural blocks attached to a paragraph are excluded). */
export function proseBlocks(doc: Doc): ProseBlock[] {
	const pp = doc.paragraphs;
	const out: ProseBlock[] = [];
	for (const p of proseParagraphs(doc)) {
		const b = doc.blocks[pp.block[p] as number];
		if (!b) continue;
		out.push({ p, base: b.charStart, text: doc.text.slice(b.charStart, b.charEnd) });
	}
	return out;
}

/** Prose-register words per paragraph (the exposure of the per-1k-words channels). */
export function wordExposure(doc: Doc): Float64Array {
	const pp = doc.paragraphs;
	const e = new Float64Array(pp.count);
	for (let p = 0; p < pp.count; p++)
		if (pp.register[p] === REGISTER_PROSE) e[p] = pp.ownWords[p] as number;
	return e;
}

/** Prose sentences (≥ 1 word) per paragraph (the exposure of the per-sentence channels). */
export function sentenceExposure(doc: Doc): Float64Array {
	const e = new Float64Array(doc.paragraphs.count);
	const s = proseSentenceList(doc);
	for (let i = 0; i < s.count; i++) {
		if ((s.words[i] as number) === 0) continue;
		const p = s.para[i] as number;
		e[p] = (e[p] as number) + 1;
	}
	return e;
}

/** Sentences of the prose list with at least one word: [listIndex]. */
export function wordSentences(doc: Doc): number[] {
	const s = proseSentenceList(doc);
	const out: number[] = [];
	for (let i = 0; i < s.count; i++) if ((s.words[i] as number) > 0) out.push(i);
	return out;
}

/** Text of prose-list sentence `i` and its absolute character range. */
export function sentenceRange(doc: Doc, i: number): { start: number; end: number } {
	const s = proseSentenceList(doc);
	const d = s.sent[i] as number;
	return { start: doc.sentences.charStart[d] as number, end: doc.sentences.charEnd[d] as number };
}

/** Count map preserving first-seen order. */
export class Tally {
	private readonly m = new Map<string, number>();
	add(key: string, n = 1): void {
		this.m.set(key, (this.m.get(key) ?? 0) + n);
	}
	get size(): number {
		return this.m.size;
	}
	/** Entries by descending count (ties: first seen first). */
	top(limit = Number.POSITIVE_INFINITY): Array<[string, number]> {
		return [...this.m.entries()].sort((a, b) => b[1] - a[1]).slice(0, limit);
	}
}

export interface DefInit {
	id: ChannelDef['id'];
	name: string;
	family: Family;
	features: ChannelDef['features'];
	direction?: 1 | -1;
	oneWay: boolean;
	minExposure: number;
	exposureUnit: ChannelDef['exposureUnit'];
	lang: ChannelDef['lang'];
	ramp: Ramp;
	unitScale: number;
	unitLabel: string;
	/** 1.5 lexical / structural, 1.2 punctuation (§3.7). */
	phi0: number;
	weight: number;
	nd: number;
	changes: string[];
	estimator?: ChannelDef['estimator'];
}

/** Rate-channel definition with the prior derived from the ramp (§3.7). */
export function rateDef(x: DefInit): ChannelDef {
	return {
		id: x.id,
		name: x.name,
		family: x.family,
		axis: 'authorship',
		registers: ['prose'],
		direction: x.direction ?? 1,
		oneWay: x.oneWay,
		estimator: x.estimator ?? 'rate',
		features: x.features,
		minExposure: x.minExposure,
		exposureUnit: x.exposureUnit,
		lang: x.lang,
		ramp: x.ramp,
		unitScale: x.unitScale,
		unitLabel: x.unitLabel,
		prior: derivePrior(x.ramp, x.unitScale, x.exposureUnit, x.phi0),
		weight: x.weight,
		nd: x.nd,
		changes: x.changes,
	};
}

/** Wrap a measurement so English-native channels honour the English gate. */
export function nativeMeasure(
	measure: (ctx: RunContext) => Measurement
): (ctx: RunContext) => Measurement {
	return (ctx) => {
		const g = englishGate(ctx.doc);
		return g ? abstainMeasurement(g) : measure(ctx);
	};
}

/** The seed's `“label” — note` evidence line. */
export const line = (label: string, note: string): string => `${label} — ${note}`;

export const quote = (s: string): string => `“${s}”`;
